import React from 'react';

import { ReactComponent as CapacityChartIcon } from '@assets/icons/capacity-chart.svg';
import { ReactComponent as SpacesIcon } from '@assets/icons/guest-room.svg';
import { useSearchForm } from '@components/SearchForm';
import Translate from '@components/Translate';
import { FlexBox, FlexBoxProps } from '@spazious/react-components';
import { styled } from '@spazious/react-styles';

import { BookingTypeValues } from './BookingTypeTypes.js';

interface TabProps {
  active: boolean;
}

interface TabsInfo {
  tabCount: number;
  hasGuestRooms: boolean;
  hasMeetingRooms: boolean;
  hasGuestRoomsAndMeetingRooms: boolean;
}

interface BookingTypeTabPanelProps extends FlexBoxProps {
  tabsInfo: TabsInfo;
  onClick: (arg1: BookingTypeValues) => void;
}

const Tab = styled.span.attrs<TabProps>(({ active }) => ({
  active,
}))<TabProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  padding: 10px 20px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 22px;
  white-space: nowrap;
  text-align: center;
  color: ${({ active }) => (active ? 'var(--primary-color)' : 'var(--secondary-color)')};
  background-color: ${({ active }) =>
    active ? 'var(--background-color)' : 'var(--background-muted)'};
  cursor: pointer;
  box-shadow: ${({ active }) => (active ? ' 0 -1px 2px 0 #efefef;' : 'none')};
  transition: all 0.2s ease;

  .full-label {
    display: inline;
  }
  .short-label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    vertical-align: middle;
    color: var(--primary-text);
  }
  .short-label {
    display: none;
  }

  &:after {
    content: '';
    position: absolute;
    height: 2px;
    width: 90%;
    background: ${({ active }) => (active ? 'var(--primary-color)' : 'transparent')};
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 14px;

    &:after {
      bottom: 5px;
      width: 85%;
    }
  }

  @media (max-width: 430px) {
    padding-top: 5px;
    flex: 1;
    .full-label {
      display: none;
    }
    .short-label {
      display: inline;
    }
    &:after {
      bottom: 4px;
      width: 80%;
    }
  }
`;

const BookingTypeTabPanel: React.FC<BookingTypeTabPanelProps> = ({
  tabsInfo,
  onClick,
  ...restPops
}) => {
  const [{ bookingType }] = useSearchForm();
  const { tabCount, hasGuestRooms, hasMeetingRooms, hasGuestRoomsAndMeetingRooms } = tabsInfo;

  return (
    <FlexBox
      alignItems="center"
      direction="row"
      flexGrow={1}
      gap="1px"
      justifyContent="flex-start"
      {...restPops}
    >
      {tabCount > 1 && (
        <>
          {hasGuestRoomsAndMeetingRooms && (
            <Tab
              active={bookingType === BookingTypeValues.AccommodationAndSpaces}
              onClick={() => onClick(BookingTypeValues.AccommodationAndSpaces)}
            >
              <span className="full-label">
                <Translate>Accommodations + Spaces</Translate>
              </span>
              <span className="short-label">
                <SpacesIcon
                  style={{
                    height: 16,
                    width: 16,
                    verticalAlign: 'middle',
                    fill: 'var(--primary-text)',
                  }}
                />
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1,
                    verticalAlign: 'middle',
                    margin: '0 5px',
                  }}
                >
                  +
                </span>
                <CapacityChartIcon
                  style={{
                    height: 16,
                    width: 16,
                    verticalAlign: 'middle',
                    fill: 'var(--primary-text)',
                  }}
                />
              </span>
            </Tab>
          )}
          {hasGuestRooms && (
            <Tab
              active={bookingType === BookingTypeValues.OnlyAccommodations}
              onClick={() => onClick(BookingTypeValues.OnlyAccommodations)}
            >
              <span className="full-label">
                <Translate>Accommodations</Translate>
              </span>
              <span className="short-label">
                <SpacesIcon
                  style={{
                    height: 16,
                    width: 16,
                    verticalAlign: 'middle',
                    fill: 'var(--primary-text)',
                  }}
                />
              </span>
            </Tab>
          )}
          {hasMeetingRooms && (
            <Tab
              active={bookingType === BookingTypeValues.OnlySpaces}
              onClick={() => onClick(BookingTypeValues.OnlySpaces)}
            >
              <span className="full-label">
                <Translate>Spaces</Translate>
              </span>
              <span className="short-label">
                <CapacityChartIcon
                  style={{
                    height: 16,
                    width: 16,
                    verticalAlign: 'middle',
                    fill: 'var(--primary-text)',
                  }}
                />
              </span>
            </Tab>
          )}
        </>
      )}
    </FlexBox>
  );
};

export default BookingTypeTabPanel;
